import {
  Component,
  computed,
  effect,
  ElementRef,
  input,
  OnDestroy,
  OnInit,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { EPlatform } from '@enums/campaign.enum';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { VideoPlayerComponent } from '@shared/components/video-player/video-player.component';
import { YoutubePlayerIframeComponent } from '@shared/components/youtube-player-iframe/youtube-player-iframe.component';
import { ICarouselConfiguration, ICarouselItem, ICarouselSlide, TSlideDirection } from './carousel.interface';

@Component({
  imports: [IconBadgeComponent, VideoPlayerComponent, YoutubePlayerIframeComponent, IconComponent],
  selector: 'app-carousel',
  styleUrl: './carousel.component.scss',
  templateUrl: './carousel.component.html',
})
export class CarouselComponent implements OnInit, OnDestroy {
  public readonly configuration = input.required<ICarouselConfiguration>();

  public readonly defaultWidth = 260 as const;
  public readonly EPlatform = EPlatform;

  public readonly slideContainer = viewChild<ElementRef<HTMLDivElement>>('slideContainer');

  public readonly currentIndex = signal<number>(0);
  public readonly previousIndex = signal<number | null>(null);
  public readonly isTransitioning = signal<boolean>(false);
  public readonly slideDirection = signal<TSlideDirection>('none');

  public readonly hasMultipleItems = computed(() => this.configuration().items.length > 1);
  public readonly currentItem = computed(() => this.configuration().items[this.currentIndex()]);

  public readonly stableHeight = computed(() => this.getStableHeight());
  public readonly previousItem = computed(() => {
    const prevIndex = this.previousIndex();

    return prevIndex !== null ? this.configuration().items[prevIndex] : null;
  });
  public readonly canGoNext = computed(() => this.currentIndex() < this.configuration().items.length - 1);
  public readonly canGoPrevious = computed(() => this.currentIndex() > 0);

  public readonly slidesToDisplay = computed(() => {
    const slides: ICarouselSlide[] = [];

    const current = this.currentItem();

    if (current) {
      slides.push({
        index: this.currentIndex(),
        item: current,
        position: 'current',
      });
    }

    const previous = this.previousItem();

    if (this.isTransitioning() && previous && this.hasMultipleItems()) {
      slides.push({
        index: this.previousIndex()!,
        item: previous,
        position: 'previous',
      });
    }

    return slides;
  });

  public readonly hasMedia = computed(() => this.slidesToDisplay().some((slide) => !!slide.item.src));

  public readonly currentCarouselHeight = signal<number | null>(null);

  private autoPlayInterval: number | null = null;

  constructor() {
    // This component can now persist across a data refresh (see
    // SocialContentTilesListComponent) instead of always being freshly constructed with
    // currentIndex at its default of 0. If a persisted instance's items array ever shrinks
    // to no longer include the currently-shown index, clamp back to a valid one instead of
    // silently rendering nothing (currentItem would otherwise resolve to undefined).
    effect(() => {
      const itemCount = this.configuration().items.length;

      untracked(() => {
        if (this.currentIndex() > itemCount - 1) {
          this.currentIndex.set(Math.max(itemCount - 1, 0));
        }
      });
    });
  }

  public ngOnInit(): void {
    if (this.configuration().autoPlay && this.hasMultipleItems()) {
      this.startAutoPlay();
    }
  }

  public ngOnDestroy(): void {
    this.stopAutoPlay();
  }

  public trackBySlideIndex(index: number, slide: { item: ICarouselItem; index: number; position: string }): string {
    return slide.item.src;
  }

  public captureCurrentHeight(element: HTMLElement | undefined): void {
    if (!element || this.isTransitioning()) {
      return;
    }

    const height = element.offsetHeight;

    if (height) {
      this.currentCarouselHeight.set(height);
    }
  }

  public getStableHeight(): number | null {
    if (this.isTransitioning()) {
      return this.currentCarouselHeight();
    }

    return null;
  }

  public goToSlide(index: number): void {
    if (index >= 0 && index < this.configuration().items.length && index !== this.currentIndex()) {
      const direction: TSlideDirection = index > this.currentIndex() ? 'right' : 'left';
      this.setCurrentIndex(index, direction);
    }
  }

  public nextSlide(): void {
    let nextIndex: number;

    if (this.canGoNext()) {
      nextIndex = this.currentIndex() + 1;
    } else if (this.hasMultipleItems()) {
      nextIndex = 0;
    } else {
      return;
    }

    this.setCurrentIndex(nextIndex, 'right');
  }

  public previousSlide(): void {
    let prevIndex: number;

    if (this.canGoPrevious()) {
      prevIndex = this.currentIndex() - 1;
    } else if (this.hasMultipleItems()) {
      prevIndex = this.configuration().items.length - 1;
    } else {
      return;
    }

    this.setCurrentIndex(prevIndex, 'left');
  }

  private setCurrentIndex(index: number, direction: TSlideDirection): void {
    if (this.isTransitioning()) {
      return;
    }

    this.captureCurrentHeight(this.slideContainer()?.nativeElement);

    this.previousIndex.set(this.currentIndex());

    this.currentIndex.set(index);
    this.slideDirection.set(direction);
    this.isTransitioning.set(true);

    setTimeout(() => {
      this.isTransitioning.set(false);
      this.slideDirection.set('none');
      this.previousIndex.set(null);
    }, 300);
  }

  private startAutoPlay(): void {
    if (this.autoPlayInterval) {
      this.stopAutoPlay();
    }

    const interval = this.configuration().autoPlayInterval ?? 5000;
    this.autoPlayInterval = window.setInterval(() => {
      this.nextSlide();
    }, interval);
  }

  private stopAutoPlay(): void {
    if (this.autoPlayInterval) {
      clearInterval(this.autoPlayInterval);
      this.autoPlayInterval = null;
    }
  }
}
